import { ChatCompletionResponse, StreamEvent } from '../types';
import { getEntityId } from './auth';

export type ActiveMind = 'hannah' | 'amanda';

const MIND_URLS: Record<ActiveMind, string> = {
  hannah: import.meta.env.VITE_HANNAH_API_URL || 'https://hannah-cortex.erikabarker7.workers.dev',
  amanda: import.meta.env.VITE_AMANDA_API_URL || 'https://amanda-cortex.erikabarker7.workers.dev',
};

export function getApiUrl(mind: ActiveMind = 'hannah'): string {
  return MIND_URLS[mind];
}

/**
 * The durable runtime accepted this turn but has not finished it (cortex runtime-gateway → hannah-runtime: JSON 202
 * `{status:'pending', eventId, detail}`). It is NOT a failure and must NOT be re-sent as a new turn: asking again with
 * the SAME request id returns this event's outcome without a second inference.
 */
export class PendingTurnError extends Error {
  /** 'unavailable': the runtime holds the event but could not read its outcome (chat.mjs JSON 503). */
  constructor(readonly eventId: string, readonly detail: string, readonly outcome: 'pending' | 'unavailable' = 'pending') {
    super(`${outcome}: ${detail}`);
    this.name = 'PendingTurnError';
  }
}

type PendingBody = { status?: unknown; eventId?: unknown; detail?: unknown };
const isPending = (b: PendingBody): b is { status: 'pending'; eventId: string; detail?: string } =>
  b.status === 'pending' && typeof b.eventId === 'string';

async function failure(response: Response): Promise<Error> {
  let detail = '';
  try {
    const body = await response.json() as PendingBody & { error?: { message?: string } | string };
    // hannah-runtime chat.mjs: 503 {status:'unavailable', eventId, detail} — a durable event, not a lost turn.
    if (body.status === 'unavailable' && typeof body.eventId === 'string') {
      return new PendingTurnError(body.eventId, typeof body.detail === 'string' ? body.detail : 'outcome unavailable', 'unavailable');
    }
    detail = typeof body.error === 'string' ? body.error : body.error?.message ?? '';
  } catch { /* not JSON: the status is the whole story */ }
  return new Error(`API error: ${response.status}${detail ? `: ${detail}` : ''}`);
}

/** One request id per user turn (`request_id` in the body, so neither cortex needs a CORS change); every
 *  transport for that turn sends the same one, which is what makes a retry the same durable event. */
function chatBody(mind: ActiveMind, messages: Array<{ role: string; content: string }>, requestId: string | undefined, extra: Record<string, unknown>) {
  return JSON.stringify({ model: mind, messages, ...(requestId ? { request_id: requestId } : {}), ...extra });
}

/**
 * Stream a message to Hannah via SSE.
 * Yields StreamEvents as they arrive.
 * Falls back to non-streaming if the server doesn't support it.
 */
export async function* streamMessage(
  messages: Array<{ role: string; content: string }>,
  token: string,
  signal?: AbortSignal,
  mind: ActiveMind = 'hannah',
  requestId?: string,
): AsyncGenerator<StreamEvent> {
  const apiUrl = getApiUrl(mind);
  const response = await fetch(`${apiUrl}/v1/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-Entity-ID': getEntityId(),
      'X-Channel': 'web'
    },
    body: chatBody(mind, messages, requestId, { stream: true }),
    signal,
  });

  if (!response.ok) {
    throw await failure(response);
  }
  // Only cortex's runtime gateway echoes the request id (runtime-gateway.ts `tagged`): the runtime has this request.
  if (requestId && response.headers.get('X-Request-Id') === requestId) yield { type: 'accepted' };

  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('text/event-stream')) {
    // Streaming response — parse SSE
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';

      for (const part of parts) {
        const dataLine = part.split('\n').find(l => l.startsWith('data: '));
        if (!dataLine) continue;
        const data = dataLine.slice(6).trim();
        if (!data || data === '[DONE]') continue;
        try {
          yield asEvent(JSON.parse(data) as StreamEvent);
        } catch {
          // Skip malformed events
        }
      }
    }

    // Process any remaining buffer
    if (buffer.trim()) {
      const dataLine = buffer.split('\n').find(l => l.startsWith('data: '));
      if (dataLine) {
        try {
          yield asEvent(JSON.parse(dataLine.slice(6).trim()) as StreamEvent);
        } catch { /* skip */ }
      }
    }
  } else {
    // Non-streaming fallback — parse as JSON
    const data = await response.json() as ChatCompletionResponse & PendingBody;
    if (isPending(data)) {
      yield { type: 'pending', eventId: data.eventId, text: data.detail ?? 'still processing' };
      return;
    }
    const text = data.choices?.[0]?.message?.content;
    if (typeof text !== 'string') throw new Error(`API error: ${response.status}: response carried no reply`);
    yield { type: 'delta', text };
    yield { type: 'done' };
  }
}

/**
 * hannah-runtime chat.mjs ends a stream it could not finish with `error` + eventId: "pending: …" (still running) or the
 * reason the outcome could not be read. Either way the event is durable; only an error WITHOUT an eventId is a failure.
 */
function asEvent(e: StreamEvent): StreamEvent {
  if (e.type === 'error' && typeof e.eventId === 'string') {
    if (e.text?.startsWith('pending:')) {
      return { type: 'pending', eventId: e.eventId, text: e.text.slice('pending:'.length).trim() || 'still processing' };
    }
    return { type: 'pending', eventId: e.eventId, text: e.text || 'outcome unavailable', outcome: 'unavailable' };
  }
  return e;
}

/**
 * Send a message without streaming (fallback).
 */
export async function sendMessage(
  messages: Array<{ role: string; content: string }>,
  token: string,
  mind: ActiveMind = 'hannah',
  requestId?: string,
): Promise<string> {
  const apiUrl = getApiUrl(mind);
  const response = await fetch(`${apiUrl}/v1/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-Entity-ID': getEntityId(),
      'X-Channel': 'web'
    },
    body: chatBody(mind, messages, requestId, { temperature: 0.7, max_tokens: 1000 }),
  });

  if (!response.ok) {
    throw await failure(response);
  }

  const data = await response.json() as ChatCompletionResponse & PendingBody;
  if (isPending(data)) throw new PendingTurnError(data.eventId, data.detail ?? 'still processing');
  const text = data.choices?.[0]?.message?.content;
  if (typeof text !== 'string') throw new Error(`API error: ${response.status}: response carried no reply`);
  return text;
}
